import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeSlash, GoogleLogo } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import IndieLawLogo from '../components/IndieLawLogo';

export default function LoginPage() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleProcessing, setGoogleProcessing] = useState(
    typeof window !== 'undefined' && window.location.hash.includes('session_id=')
  );

  // Returning from Google OAuth: exchange the session_id, then route into /admin.
  useEffect(() => {
    const hash = window.location.hash || '';
    if (!hash.includes('session_id=')) return;
    const sid = new URLSearchParams(hash.replace(/^#/, '')).get('session_id');
    window.history.replaceState(null, '', window.location.pathname);
    (async () => {
      const res = await loginWithGoogle(sid);
      if (res.success) {
        navigate('/admin');
      } else {
        setError(res.error || 'Google sign-in failed.');
        setGoogleProcessing(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  if (googleProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="google-processing">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-[#141b4d]/20 border-t-[#141b4d] rounded-full animate-spin"></div>
          <p className="text-sm" style={{ color: '#52525B' }}>Signing you in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{ backgroundColor: '#141b4d' }}
      >
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <IndieLawLogo variant="light" className="h-20 w-auto" />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-6"
          >
            <h1 className="text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight" style={{ fontFamily: 'Arial, sans-serif' }}>
              Your Trademark<br />
              <span style={{ color: '#ffd100' }}>Status at a Glance</span>
            </h1>
            <p className="text-white/70 text-lg max-w-md leading-relaxed">
              Track the progress of your trademark applications, view important documents, and stay connected with your legal team.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-white/40 text-sm"
          >
            &copy; {new Date().getFullYear()} Indie Law. All rights reserved.
          </motion.div>
        </div>
        {/* Yellow accent stripe */}
        <div className="absolute bottom-0 left-0 right-0 h-1" style={{ backgroundColor: '#ffd100' }}></div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden mb-12">
            <IndieLawLogo variant="dark" className="h-16 w-auto" />
          </div>

          <div className="mb-8">
            <p className="label-uppercase mb-2" style={{ color: '#1d57a5' }}>Client Portal</p>
            <h2 className="text-3xl font-bold tracking-tight" style={{ color: '#141b4d', fontFamily: 'Arial, sans-serif' }}>
              Welcome Back
            </h2>
          </div>

          {/* Indie Law staff — Google sign-in only */}
          <div className="mb-6">
            <button
              type="button"
              onClick={handleGoogle}
              data-testid="google-signin-btn"
              className="w-full flex items-center justify-center gap-3 border border-[#E4E4E7] bg-white py-3 text-sm font-semibold transition-colors hover:bg-[#FAFAF8]"
              style={{ color: '#141b4d' }}
            >
              <GoogleLogo weight="bold" className="w-5 h-5" />
              Indie Law staff — Sign in with Google
            </button>
          </div>

          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-[#E4E4E7]"></div>
            <span className="text-xs uppercase tracking-wide" style={{ color: '#A1A1AA' }}>Client sign in</span>
            <div className="flex-1 h-px bg-[#E4E4E7]"></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm"
                data-testid="login-error"
              >
                {error}
              </motion.div>
            )}

            <div>
              <label className="label-uppercase block mb-2" htmlFor="email">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-swiss"
                placeholder="you@example.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="label-uppercase block mb-2" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-swiss pr-12"
                  placeholder="Enter your password"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#09090B] transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? (
                    <EyeSlash weight="regular" className="w-5 h-5" />
                  ) : (
                    <Eye weight="regular" className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="text-right">
              <Link to="/reset-password" className="text-sm font-medium hover:underline" style={{ color: '#1d57a5' }}
                data-testid="forgot-password-link">
                Forgot your password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-[#09090B]/20 border-t-[#09090B] rounded-full animate-spin"></div>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-[#52525B]">
            Don't have an account?{' '}
            <Link to="/get-started" className="text-[#09090B] font-medium hover:underline" data-testid="login-get-started-link">
              Get started
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
